import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Footer from "../../Shared/Footer/Footer";
import "./DetailedProject.css";

const toSlug = (value = "") =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "").trim();

const DetailedProject = () => {
  const { name } = useParams();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetch("/data-json/details.json")
      .then((res) => res.json())
      .then((data) => {
        setProjects(Array.isArray(data) ? data : []);
      });
  }, []);

  const project = useMemo(
    () =>
      projects.find((item) => {
        const bySlug = item?.slug || toSlug(item?.title);
        return bySlug === name;
      }),
    [projects, name]
  );

  const otherProjects = useMemo(
    () =>
      projects.filter((item) => {
        const bySlug = item?.slug || toSlug(item?.title);
        return bySlug !== name;
      }),
    [projects, name]
  );

  if (!project) {
    return (
      <div className="project-details">
        <div className="project-details__shell">
          <div className="project-details__top">
            <Link to="/">{"< Back to selected work"}</Link>
          </div>
          <p className="project-details__loading">Loading case study...</p>
        </div>
      </div>
    );
  }

  const caseStudy = project.case_study || {};
  const summaryLinks = caseStudy.summary_links || [];
  const sections = caseStudy.sections || [];

  return (
    <div className="project-details">
      <div className="project-details__shell">
        <header className="project-details__top">
          <div className="project-details__crumb">
            <Link to="/">{"< Back to selected work"}</Link>
            <span>/</span>
            <strong>Project Detail</strong>
          </div>
        </header>

        <div className="project-details__rule" />

        <section className="project-hero">
          <div className="project-hero__main">
            <p className="project-hero__label">{caseStudy.label || "Case Study"}</p>
            <h1>{caseStudy.headline || project.title}</h1>
            <p className="project-hero__lede">
              {caseStudy.lede || project.long_desc_intro}
            </p>
            <p className="project-hero__meta">
              <span>Role: {caseStudy?.meta?.role || "Product + Engineering"}</span>
              <span aria-hidden="true">•</span>
              <span>Timeline: {caseStudy?.meta?.timeline || "In progress"}</span>
            </p>
          </div>

          <aside className="project-hero__aside">
            <p className="project-hero__status-label">Status</p>
            <h2>{caseStudy.status || "Live"}</h2>
            <div className="project-hero__links">
              {summaryLinks.map((item) => (
                <a key={item.label} href={item.url} target="_blank" rel="noreferrer">
                  {item.label} {"->"}
                </a>
              ))}
            </div>
          </aside>
        </section>

        <div className="project-details__rule" />

        <section className="project-layout">
          <div className="project-layout__main">
            {sections.map((section) => (
              <article className="project-card" key={section.title}>
                <h3>{section.title}</h3>
                {section.body ? <p>{section.body}</p> : null}
                {section.bullets?.length ? (
                  <ul>
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>

          <aside className="project-layout__rail">
            <h3>Switch project</h3>
            <div className="project-switch">
              {otherProjects.map((other) => {
                const slug = other?.slug || toSlug(other?.title);
                return (
                  <Link key={other.id} className="project-switch__btn" to={`/details/${slug}`}>
                    <span>{other.title}</span>
                    <span aria-hidden="true">{"->"}</span>
                  </Link>
                );
              })}
            </div>
            <p className="project-layout__note">
              {caseStudy.switch_note || "[ This page is under active editorial rewrite ]"}
            </p>
          </aside>
        </section>

        <div className="project-details__rule" />

        <section className="project-quote">
          <blockquote>"{caseStudy.quote}"</blockquote>
          <p>- {caseStudy.quote_by}</p>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default DetailedProject;